/**
 * Why a speed test scored what it did (Brandy, October 6, 2026).
 *
 * Her October 5 test on an RTX 4080 scored 8 and finished 2 pages in three minutes, and nobody
 * could say why: the test's own engine log lives in its private workspace, which is removed when
 * the test ends. Every run now keeps this compact record with its result instead: which device
 * each part read on and the reason the engine gave, the free graphics memory it started with and
 * the width that followed, what finished when, whether someone used the computer, and what
 * failed. It is a few kilobytes, kept beside the saved result (`benchmark-details/`), and the
 * numbers and code words in it are sent to Ademero with the result (`speedTestTelemetryAttrs`).
 *
 * No Node or Electron imports: the renderer shows `speedTestWhyLines` under the score.
 */

import type { AcceleratorVerdict } from './desktop-api';

export type SpeedTestDevice = 'graphics' | 'processor' | 'unknown';

/** One part of the reading pipeline, as the engine's computer check reported it before the timer started. */
export type SpeedTestPart = {
  /** The computer check's part name: `ocr` (reading the words) or `extraction` (understanding the document). */
  readonly component: string;
  readonly device: SpeedTestDevice;
  readonly verdict: AcceleratorVerdict | null;
  /** The engine's own plain reason, when it gave one (trimmed). */
  readonly reason: string | null;
};

/** One request the test made, timed from the start of the three-minute window (negative before it). */
export type SpeedTestStep = {
  readonly step: 'setup' | 'images' | 'read' | 'understand' | 'finish';
  readonly files: number;
  readonly startMs: number;
  /** Null: still going when the three minutes ended. */
  readonly endMs: number | null;
  readonly pages?: number;
  readonly failed?: true;
};

/** What the test engine's own log said, read before its workspace is removed. */
export type SpeedTestEngineFacts = {
  /** Each start of the document reader: its device, how many documents at once, free card memory it measured. */
  readonly readerStarts: readonly {
    readonly device: string;
    readonly slots: number;
    readonly freeGraphicsMb: number | null;
  }[];
  /** The card was crowded, so the reader started narrower than planned. */
  readonly readerCrowded: {
    readonly freeGraphicsMb: number;
    readonly slots: number;
    readonly of: number;
  } | null;
  readonly readerStartFailures: number;
  /** Times the document reader was stopped so reading the words could have the card. */
  readonly readerStoppedForReading: number;
  /** The width reading the words got for the test's pages, and the free card memory it was planned from. */
  readonly readingLanes: { readonly lanes: number; readonly freeGraphicsMb: number } | null;
  readonly readingWarmupMs: number | null;
  readonly readingWarmed: { readonly warmed: number; readonly usable: number } | null;
  /** The engine's sentence for planning reading on the processor, when it said one. */
  readonly readingOnProcessor: string | null;
  /** A reading lane wanted the card but there was not enough free memory, so it read on the processor. */
  readonly graphicsRefused: number;
  readonly rescuedOnProcessor: number;
  /** Times reading held back because someone was using the computer. */
  readonly courtesyRests: number;
  /** Width cuts by cause: `person` (someone started using the computer), `memory`, `processor`. */
  readonly widthChanges: Readonly<Record<string, number>>;
  readonly failures: number;
  readonly extractionProfile: string | null;
};

export type SpeedTestWhy = {
  readonly version: 1;
  /** The person's saved Processing mode, and the mode the test ran: always Automatic, so scores compare. */
  readonly processingMode: { readonly saved: string; readonly tested: 'auto' };
  /** Starting the readers, before the timer (never counted in the three minutes). */
  readonly preparationMs: number | null;
  /** From the start of the three minutes to the first finished document. */
  readonly firstPageMs: number | null;
  /** Pages the words were read on within the three minutes, and pages in finished documents (the score's count). */
  readonly pagesRead: number;
  readonly pagesFinished: number;
  /** Time on each step the reader reported (preparing, recognizing, understanding, ...). */
  readonly stepMs: Readonly<Record<string, number>>;
  readonly steps: readonly SpeedTestStep[];
  /**
   * Each document that finished in the three minutes, in order: when (from the start of the window)
   * and its pages. Measures a step's steady speed, even inside a wave the deadline cut off
   * (docs/planning/speed-test-and-group-sizes.md, option A). Absent before October 7, 2026.
   */
  readonly finished?: readonly { readonly ms: number; readonly pages: number }[];
  readonly parts: readonly SpeedTestPart[];
  readonly graphicsMemory: {
    readonly totalMb: number | null;
    readonly freeAtStartMb: number | null;
  };
  readonly readingLanes: number | null;
  readonly readerSlots: number | null;
  /** Seconds of the three minutes in which the computer was in use (keyboard or mouse in the last 30 seconds). */
  readonly personActiveSeconds: number | null;
  /** Null when the test engine's log could not be read. */
  readonly engine: SpeedTestEngineFacts | null;
  readonly errors: readonly string[];
  /** The test engine's own lines behind these facts (privacy-typed), at most 20. Kept on this computer only. */
  readonly lines: readonly string[];
};

export type SpeedTestWhyLine = {
  readonly icon: 'apps' | 'processor' | 'person' | 'card';
  readonly text: string;
};

/** Shown above the lines: they say what may have affected the result, never how the test works. */
export const SPEED_TEST_WHY_HEADING = 'What may have slowed it down:';

/**
 * The plain lines under a low score (below the strong band; the reference desktop with a card
 * scores 80). At most three, each something the person can relate to and the record shows
 * (Brandy, October 6, 2026): never the test's mechanics (batches, files counted, lanes, pages still
 * being read at the deadline, warm-up). The full why stays in the record and what is sent.
 */
export function speedTestWhyLines(
  why: SpeedTestWhy | null | undefined,
  context: { readonly score: number | null; readonly graphicsCard: boolean },
): SpeedTestWhyLine[] {
  if (!why || why.version !== 1 || context.score === null || context.score >= 60) return [];
  const lines: SpeedTestWhyLine[] = [];
  const engine = why.engine;
  const part = (component: string) => why.parts.find((value) => value.component === component);
  const readerOnGraphics = Boolean(
    engine?.readerStarts.some((start) => /cuda|vulkan|directml/i.test(start.device)),
  );
  // A card is fitted when the test record names one, or the test engine read on one or said one
  // should have been used (running-on-cpu means a card is fitted and that part is not on it).
  const cardFitted =
    context.graphicsCard ||
    readerOnGraphics ||
    why.parts.some(
      (value) =>
        value.device === 'graphics' ||
        value.verdict === 'verified-gpu' ||
        value.verdict === 'running-on-cpu',
    );
  if (cardFitted) {
    // Only the engine's own evidence says the card was short: a refused graphics start, a reader
    // start that failed or started narrower for lack of free memory.
    const cardShort = Boolean(
      engine &&
      (engine.graphicsRefused > 0 || engine.readerStartFailures > 0 || engine.readerCrowded),
    );
    const readerOnCard = part('extraction')?.device === 'graphics' || readerOnGraphics;
    const someOnProcessor =
      part('ocr')?.device === 'processor' ||
      part('extraction')?.device === 'processor' ||
      Boolean(engine?.readingOnProcessor) ||
      (engine?.graphicsRefused ?? 0) > 0;
    if (cardShort) lines.push({ icon: 'apps', text: 'Other apps were using the graphics card.' });
    // Busy is said only when the record shows what kept it busy: other apps, or the document reader.
    if (someOnProcessor && (cardShort || readerOnCard))
      lines.push({
        icon: 'processor',
        text: 'The graphics card was busy, so some reading ran on the processor.',
      });
  } else if (why.parts.length > 0) {
    // The test engine's computer check looked and found no card it reads on.
    lines.push({ icon: 'card', text: 'This computer has no supported graphics card.' });
  }
  if ((engine?.courtesyRests ?? 0) > 0 || (engine?.widthChanges.person ?? 0) > 0)
    lines.push({ icon: 'person', text: 'The computer was in use during the test.' });
  return lines.slice(0, 3);
}

/** Phone Home's attribute limits (capturepoint-v6-telemetry-policy): at most 24, strings up to 160. */
export type SpeedTestTelemetryAttrs = Record<string, string | number | boolean | null>;

/**
 * What is sent to Ademero with a speed test (product telemetry, `perf` / `speed-test.result`): the
 * outcome, the score and pages, and the why as numbers and code words. Never the log lines, the
 * engine's reasons as written, or anything from a document.
 */
export function speedTestTelemetryAttrs(input: {
  readonly outcome: 'finished' | 'stopped' | 'failed';
  readonly score: number | null;
  readonly pagesPerMinute: number | null;
  readonly why: SpeedTestWhy;
  /** The plain sentence of a stop or failure, without its technical details. */
  readonly reason?: string | null;
}): SpeedTestTelemetryAttrs {
  const { why } = input;
  const engine = why.engine;
  const part = (component: string) => why.parts.find((value) => value.component === component);
  const unfinished = why.steps
    .filter((step) => step.endMs === null && step.step !== 'setup')
    .reduce((most, step) => Math.max(most, step.files), 0);
  const attrs: SpeedTestTelemetryAttrs = {
    outcome: input.outcome,
    score: input.score,
    pages: why.pagesFinished,
    pages_read: why.pagesRead,
    ppm: input.pagesPerMinute === null ? null : Math.round(input.pagesPerMinute * 100) / 100,
    mode_saved: why.processingMode.saved,
    ocr_device: part('ocr')?.device ?? 'unknown',
    ocr_verdict: part('ocr')?.verdict ?? null,
    reader_device: part('extraction')?.device ?? 'unknown',
    reader_verdict: part('extraction')?.verdict ?? null,
    vram_total_mb: why.graphicsMemory.totalMb,
    vram_free_start_mb: why.graphicsMemory.freeAtStartMb,
    ocr_lanes: why.readingLanes,
    ocr_phase_lanes: engine?.readingLanes?.lanes ?? null,
    reader_slots: why.readerSlots,
    prep_ms: why.preparationMs === null ? null : Math.round(why.preparationMs),
    first_page_ms: why.firstPageMs === null ? null : Math.round(why.firstPageMs),
    unfinished_files: unfinished,
    person_active_s: why.personActiveSeconds,
    courtesy_rests: engine?.courtesyRests ?? null,
    gpu_refused: engine ? engine.graphicsRefused + engine.readerStartFailures : null,
    failures: (engine?.failures ?? 0) + why.errors.length,
    ocr_on_cpu_planned: engine ? Boolean(engine.readingOnProcessor) : null,
  };
  if (input.reason) attrs.reason = speedTestReasonCode(input.reason);
  return attrs;
}

/**
 * Why a test stopped or failed, as a code word. The sentence itself is not sent: a failed step's
 * words can be the engine's own, which can name the person's things.
 */
export function speedTestReasonCode(
  reason: string,
): 'processing-started' | 'person' | 'app-closing' | 'samples-missing' | 'step-failed' | 'other' {
  if (/started processing/i.test(reason)) return 'processing-started';
  if (/at your request/i.test(reason)) return 'person';
  if (/application is closing/i.test(reason)) return 'app-closing';
  if (/sample documents/i.test(reason)) return 'samples-missing';
  if (/could not finish a processing step/i.test(reason)) return 'step-failed';
  return 'other';
}
